import type { AffiliateConfig } from '@/lib/types/tenant';
import { matchesUrlPattern } from '@/lib/utils/domain';

export const AFFILIATE_STORAGE_KEY = 'affiliate_ref';
export const AFFILIATE_REDIRECT_PARAM = '__aff';

const DEFAULT_AFFILIATE_PREFIX = 'bazak_';
export const BAZAK_AFFILIATE_PREFIXES = ['bzk_', DEFAULT_AFFILIATE_PREFIX] as const;
// Stops a crafted referrer from persisting a huge string in the year-long override cookie.
export const BAZAK_AFFILIATE_MAX_LENGTH = 128;
const BAZAK_AFFILIATE_VALUE_PATTERN = /^[A-Za-z0-9_-]+$/;

function hasAffiliateIdShape(value: string | null | undefined): value is string {
  return (
    !!value &&
    value.length <= BAZAK_AFFILIATE_MAX_LENGTH &&
    BAZAK_AFFILIATE_VALUE_PATTERN.test(value)
  );
}

function startsWithAny(value: string, prefixes: readonly string[]): boolean {
  const lower = value.toLowerCase();
  return prefixes.some(prefix => lower.startsWith(prefix.toLowerCase()));
}

export function isBazakAffiliateValue(value: string | null | undefined): boolean {
  return hasAffiliateIdShape(value) && startsWithAny(value, BAZAK_AFFILIATE_PREFIXES);
}

export function getTargetParam(config: AffiliateConfig): string {
  return config.targetParam || config.paramName || 'ref';
}

export function shouldApplyAffiliate({
  url,
  config,
}: {
  url: string;
  config?: AffiliateConfig;
}): boolean {
  if (!config?.applyToPatterns?.length) return false;
  return config.applyToPatterns.some(pattern => matchesUrlPattern({ url, pattern }));
}

function affiliatePrefix(config?: AffiliateConfig): string {
  return config?.prefix ?? DEFAULT_AFFILIATE_PREFIX;
}

function isOwnAffiliateId({ value, config }: { value: string; config?: AffiliateConfig }): boolean {
  return startsWithAny(value, [
    affiliatePrefix(config),
    ...BAZAK_AFFILIATE_PREFIXES,
    ...(config?.validPrefixes ?? []),
  ]);
}

/**
 * The affiliate ID to propagate, or null when it isn't shaped like one.
 *
 * Partner pixels only fire for IDs carrying our prefix, so a ref arriving without one is
 * adopted under the tenant's prefix rather than dropped. The adopted form is what gets
 * persisted and put on outgoing links, so it has to clear the shape gate too.
 */
export function resolveAffiliateId({
  value,
  config,
}: {
  value: string | null;
  config?: AffiliateConfig;
}): string | null {
  if (!hasAffiliateIdShape(value)) return null;
  if (isOwnAffiliateId({ value, config })) return value;

  const adopted = `${affiliatePrefix(config)}${value}`;
  return hasAffiliateIdShape(adopted) ? adopted : null;
}

export function resolveBestAffiliateId({
  values,
  config,
}: {
  values: string[];
  config?: AffiliateConfig;
}): string {
  const best = values.find(value => isOwnAffiliateId({ value, config })) ?? values[0];
  return resolveAffiliateId({ value: best, config }) ?? best;
}
